import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, GraduationCap, DollarSign, Globe, Book, Bookmark, X } from 'lucide-react';
import ScholarshipCard from '../components/scholarships/ScholarshipCard';
import Button from '../components/ui/Button';

const placeholderScholarships = [
  {
    id: '1',
    name: 'STEM Excellence Scholarship',
    description: 'Supporting outstanding students pursuing degrees in Science, Technology, Engineering, and Mathematics.',
    amount: 25000,
    deadline: '2025-05-01',
    eligibility_criteria: {},
    provider: 'National Science Foundation',
    countries: ['USA', 'Canada'],
    degree_types: ['Bachelors', 'Masters'],
    fields_of_study: ['Computer Science', 'Engineering', 'Mathematics', 'Physics']
  },
  {
    id: '2',
    name: 'Global Business Leaders Grant',
    description: 'Empowering future business leaders with international perspective and innovative thinking.',
    amount: 20000,
    deadline: '2025-06-01',
    eligibility_criteria: {},
    provider: 'International Business School Alliance',
    countries: ['Global'],
    degree_types: ['Bachelors', 'Masters', 'MBA'],
    fields_of_study: ['Business Administration', 'Economics', 'Finance', 'International Relations']
  },
  {
    id: '3',
    name: 'Creative Arts Fellowship',
    description: 'Supporting emerging artists and creative professionals in pursuing their educational goals.',
    amount: 15000,
    deadline: '2025-07-15',
    eligibility_criteria: {},
    provider: 'Arts Council',
    countries: ['USA', 'UK', 'Australia'],
    degree_types: ['Bachelors', 'Masters'],
    fields_of_study: ['Fine Arts', 'Design', 'Music', 'Film Studies']
  },
  {
    id: '4',
    name: 'Healthcare Innovation Scholarship',
    description: 'Supporting students committed to advancing healthcare through innovation and research.',
    amount: 30000,
    deadline: '2025-08-30',
    eligibility_criteria: {},
    provider: 'Healthcare Foundation',
    countries: ['USA', 'Canada', 'UK'],
    degree_types: ['Masters', 'PhD'],
    fields_of_study: ['Medicine', 'Nursing', 'Public Health', 'Biomedical Engineering']
  }
];

const Scholarships: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [selectedDegrees, setSelectedDegrees] = useState<string[]>([]);
  const [showBookmarked, setShowBookmarked] = useState(false);
  const [bookmarkedScholarships, setBookmarkedScholarships] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const filteredScholarships = placeholderScholarships.filter((scholarship) => {
    const matchesSearch = 
      scholarship.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scholarship.provider.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFields =
      selectedFields.length === 0 ||
      scholarship.fields_of_study.some((field) => selectedFields.includes(field));

    const matchesDegrees =
      selectedDegrees.length === 0 ||
      scholarship.degree_types.some((degree) => selectedDegrees.includes(degree));

    const matchesBookmarked = 
      !showBookmarked || bookmarkedScholarships.includes(scholarship.id);

    return matchesSearch && matchesFields && matchesDegrees && matchesBookmarked;
  });

  const allFields = Array.from(
    new Set(placeholderScholarships.flatMap((s) => s.fields_of_study))
  );

  const allDegrees = Array.from(
    new Set(placeholderScholarships.flatMap((s) => s.degree_types))
  );

  const toggleBookmark = (scholarshipId: string) => {
    setBookmarkedScholarships(prev =>
      prev.includes(scholarshipId)
        ? prev.filter(id => id !== scholarshipId)
        : [...prev, scholarshipId]
    );
  };

  const clearFilters = () => {
    setSelectedFields([]);
    setSelectedDegrees([]);
    setShowBookmarked(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Hero Section */}
          <div className="relative bg-gradient-to-b from-primary-900 to-primary-800 py-16 mb-12 rounded-3xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-700 rounded-full mix-blend-multiply opacity-20 animate-blob"></div>
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute top-40 left-1/2 transform -translate-x-1/2 w-80 h-80 bg-accent-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
                  Find Your Perfect Scholarship
                </h1>
                <p className="text-xl text-primary-100 max-w-2xl mx-auto mb-8">
                  Discover scholarships tailored to your academic journey and make your educational dreams a reality.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <DollarSign className="h-5 w-5 mr-2" />
                    <span>Financial Aid</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Globe className="h-5 w-5 mr-2" />
                    <span>Global Opportunities</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Book className="h-5 w-5 mr-2" />
                    <span>All Fields of Study</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                {/* Search Input */}
                <div className="flex-1 w-full">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      placeholder="Search scholarships by name or provider..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input pl-12 w-full bg-gray-50 border-0 focus:ring-2"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 w-full md:w-auto">
                  <Button
                    variant={showBookmarked ? 'primary' : 'outline'}
                    onClick={() => setShowBookmarked(!showBookmarked)}
                    icon={<Bookmark className="h-4 w-4" />}
                    className="flex-1 md:flex-none"
                  >
                    Saved
                  </Button>
                  <Button
                    variant={showFilters ? 'primary' : 'outline'}
                    onClick={() => setShowFilters(!showFilters)}
                    icon={<Filter className="h-4 w-4" />}
                    className="flex-1 md:flex-none"
                  >
                    Filters
                  </Button>
                </div>
              </div>

              {/* Filter Section */}
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-6 pt-6 border-t"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                    {(selectedFields.length > 0 || selectedDegrees.length > 0) && (
                      <button
                        onClick={clearFilters}
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Clear all filters
                      </button>
                    )}
                  </div>

                  {/* Fields of Study */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Fields of Study</h4>
                    <div className="flex flex-wrap gap-2">
                      {allFields.map((field) => (
                        <button
                          key={field}
                          onClick={() =>
                            setSelectedFields((prev) =>
                              prev.includes(field)
                                ? prev.filter((f) => f !== field)
                                : [...prev, field]
                            )
                          }
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                            selectedFields.includes(field)
                              ? 'bg-primary-100 text-primary-800 ring-1 ring-primary-500'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {field}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Degree Types */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Degree Level</h4>
                    <div className="flex flex-wrap gap-2">
                      {allDegrees.map((degree) => (
                        <button
                          key={degree}
                          onClick={() =>
                            setSelectedDegrees((prev) =>
                              prev.includes(degree)
                                ? prev.filter((d) => d !== degree)
                                : [...prev, degree]
                            )
                          }
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                            selectedDegrees.includes(degree)
                              ? 'bg-secondary-100 text-secondary-800 ring-1 ring-secondary-500'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {degree}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {filteredScholarships.length} {filteredScholarships.length === 1 ? 'Scholarship' : 'Scholarships'} Available
              </h2>
              <div className="text-sm text-gray-500">
                Updated daily
              </div>
            </div>
          </motion.div>

          {/* Scholarship Cards */}
          <motion.div
            variants={containerVariants}
            className="grid md:grid-cols-2 gap-8"
          >
            {filteredScholarships.map((scholarship) => (
              <ScholarshipCard
                key={scholarship.id}
                scholarship={scholarship}
                isBookmarked={bookmarkedScholarships.includes(scholarship.id)}
                onToggleBookmark={() => toggleBookmark(scholarship.id)}
              />
            ))}
          </motion.div>

          {/* Empty State */}
          {filteredScholarships.length === 0 && (
            <motion.div
              variants={itemVariants}
              className="text-center py-16 bg-white rounded-2xl shadow-md"
            >
              <GraduationCap className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No scholarships found</h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your filters or search terms to find more opportunities.
              </p>
              <Button
                variant="outline"
                onClick={clearFilters}
                icon={<X className="h-4 w-4" />}
              >
                Clear all filters
              </Button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Scholarships;